import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { assetService } from '@/services/asset-service';
import { toast } from 'sonner';

interface TextEditorProps {
  assetId: string;
}

export const TextEditor = ({ assetId }: TextEditorProps) => {
  const [textContent, setTextContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const fetchTextContent = async () => {
      try {
        setLoading(true);
        const asset = await assetService.getAsset(assetId);
        
        // Text assets now store content in R2 with URI reference in data field
        if (asset.data && typeof asset.data === 'object' && 'uri' in asset.data) {
          // Fetch the content from the R2 URI
          const uri = asset.data.uri as string;
          const response = await fetch(uri);
          if (!response.ok) {
            throw new Error(`Failed to fetch content from ${uri}`);
          }
          const content = await response.text();
          setTextContent(content);
        } else {
          // Legacy support for old format (should not occur with new assets)
          console.warn('Asset does not have URI in data field:', asset);
          setTextContent('');
        }
      } catch (err) {
        console.error('Error fetching text content:', err);
        toast.error('Failed to load text content');
      } finally {
        setLoading(false);
      }
    };

    fetchTextContent();
  }, [assetId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      // Upload text content to R2 and create new version
      // This follows the same pattern as initial creation - content is stored in R2, not in database
      await assetService.updateAssetContent(assetId, textContent, 'content.txt');
      toast.success('Text content saved successfully');
    } catch (err) {
      console.error('Error saving text content:', err);
      toast.error('Failed to save text content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex space-x-2 mb-4">
        <Button
          variant={isEditing ? "default" : "outline"}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
        <Button
          variant={!isEditing ? "default" : "outline"}
          onClick={() => setIsEditing(false)}
        >
          Preview
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <Textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            className="min-h-[400px] font-mono text-sm"
            placeholder="Write your text here..."
          />
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg border min-h-[400px] whitespace-pre-wrap">
          {textContent || <span className="text-gray-500 italic">No content to display</span>}
        </div>
      )}
    </div>
  );
};